/**
 * 원본 자산 링크 입력 (261010-agency-console · 운영자 결정 2026-10-11):
 *   대표 웹페이지(`homepageUrl`) **필수** — 좁은 뜻의 홈페이지가 아니라 고객이 가장 주로 쓰는 웹페이지(네이버 블로그 등) · 네이버 플레이스 **선택·권장** · 기타 링크 **선택(콘텐츠 작성 참고용)**
 *
 * 브랜드를 만드는 화면(가입·브랜드 추가)과 고치는 화면이 같이 쓴다.
 * ★ 홈페이지 필수는 서버도 본다(계약 `AssetLinks`). 이 부품의 검사는 편의다.
 * ★ 사람들은 `blog.naver.com/xxx` 처럼 주소 앞을 빼고 붙여 넣는다 — 보낼 때 `https://` 를 붙인다.
 */
import { HOMEPAGE_REQUIRED_MESSAGE, LINKS_MAX } from '@geo-admin/shared/contract/api/registry.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Input, Label } from './ui/form.tsx';

export interface LinksValue {
  homepageUrl: string;
  naverPlaceUrl: string;
  links: string[];
}

/** 기타 링크는 칸 없이 시작한다 — 「+ 링크 추가」를 누르면 열린다 */
export const emptyLinks = (): LinksValue => ({ homepageUrl: '', naverPlaceUrl: '', links: [] });

export function linksFrom(v: {
  homepageUrl: string | null;
  naverPlaceUrl: string | null;
  links: string[];
}): LinksValue {
  return {
    homepageUrl: v.homepageUrl ?? '',
    naverPlaceUrl: v.naverPlaceUrl ?? '',
    links: [...v.links],
  };
}

/** 주소 앞(`https://`)이 빠졌으면 붙인다. 빈 값은 빈 값 */
export function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

const looksLikeUrl = (u: string) => /^https?:\/\/[^\s/]+\.[^\s]+$/i.test(u);

/** 보낼 본문 — 빈 칸을 빼고 주소 앞을 채운다 */
export function linksPayload(v: LinksValue): { homepageUrl: string; naverPlaceUrl: string | null; links: string[] } {
  return {
    homepageUrl: normalizeUrl(v.homepageUrl),
    naverPlaceUrl: normalizeUrl(v.naverPlaceUrl) || null,
    links: v.links.map(normalizeUrl).filter(Boolean),
  };
}

/** 문제가 있으면 사람이 읽을 문장, 없으면 null */
export function linksProblem(v: LinksValue): string | null {
  const p = linksPayload(v);
  const all = [p.homepageUrl, p.naverPlaceUrl, ...p.links].filter((u): u is string => Boolean(u));
  if (!p.homepageUrl) return HOMEPAGE_REQUIRED_MESSAGE;
  const bad = all.find((u) => !looksLikeUrl(u));
  return bad ? `주소 형식이 아닙니다: ${bad}` : null;
}

/** 주소로 종류를 짐작한다 — 표시용이다(저장은 주소만) */
export function linkKind(url: string): string | null {
  let host = '';
  try {
    host = new URL(normalizeUrl(url)).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
  if (/(^|\.)place\.naver\.com$|^map\.naver\.com$|^naver\.me$/.test(host)) return '네이버 플레이스';
  if (/^(m\.)?blog\.naver\.com$/.test(host)) return '네이버 블로그';
  if (/(^|\.)tistory\.com$/.test(host)) return '티스토리';
  if (/(^|\.)instagram\.com$/.test(host)) return '인스타그램';
  if (/(^|\.)facebook\.com$|^fb\.com$/.test(host)) return '페이스북';
  if (/(^|\.)youtube\.com$|^youtu\.be$/.test(host)) return '유튜브';
  if (/^pf\.kakao\.com$/.test(host)) return '카카오 채널';
  if (/(^|\.)threads\.net$/.test(host)) return '스레드';
  if (/^(x|twitter)\.com$/.test(host)) return 'X';
  return null;
}

export function LinksField({
  value,
  onChange,
  showProblem,
  className,
  required = true,
}: {
  value: LinksValue;
  onChange: (v: LinksValue) => void;
  /** 제출을 한 번 시도한 뒤에만 문제를 보인다 — 처음부터 빨간 글씨를 띄우지 않는다 */
  showProblem?: boolean;
  className?: string;
  /** 운영자 화면은 필수가 아니다(준비 중인 고객을 먼저 만든다) */
  required?: boolean;
}) {
  const set = (patch: Partial<LinksValue>) => onChange({ ...value, ...patch });
  const setLink = (i: number, v: string) => set({ links: value.links.map((l, j) => (j === i ? v : l)) });
  const problem = showProblem ? linksProblem(value) : null;

  return (
    <div className={cn('grid gap-3', className)}>
      <Label>
        <span>
          대표 웹페이지 {required ? <span className="text-bad">*</span> : null}{' '}
          <span className="font-normal text-slate-500 text-xs">
            {required ? '필수 · ' : ''}홈페이지·네이버 블로그 등 가장 주로 쓰는 곳 하나
          </span>
        </span>
        <Input
          inputMode="url"
          placeholder="https://"
          value={value.homepageUrl}
          onChange={(e) => set({ homepageUrl: e.target.value })}
        />
      </Label>
      <Label>
        <span>
          네이버 플레이스 <span className="font-normal text-slate-500 text-xs">선택, 권장</span>
        </span>
        <Input
          inputMode="url"
          placeholder="https://map.naver.com/… 또는 naver.me/…"
          value={value.naverPlaceUrl}
          onChange={(e) => set({ naverPlaceUrl: e.target.value })}
        />
      </Label>
      <div className="grid gap-2">
        <span className="font-medium text-sm">
          기타 링크{' '}
          <span className="font-normal text-slate-500 text-xs">
            블로그·인스타그램·페이스북·유튜브·카카오 채널 등 GEO 컨텐츠 작성 참고용
          </span>
        </span>
        {value.links.map((l, i) => {
          const kind = l.trim() ? linkKind(l) : null;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: 입력 칸 순서가 곧 식별자다(값은 바뀌는 중이다)
            <div key={i} className="flex items-center gap-2">
              <Input
                inputMode="url"
                placeholder="https://"
                value={l}
                onChange={(e) => setLink(i, e.target.value)}
                aria-label={`링크 ${i + 1}`}
              />
              {kind ? <span className="shrink-0 text-slate-500 text-xs">{kind}</span> : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="이 링크 지우기"
                onClick={() => set({ links: value.links.filter((_, j) => j !== i) })}
              >
                ✕
              </Button>
            </div>
          );
        })}
        {value.links.length < LINKS_MAX ? (
          <div>
            <Button type="button" size="sm" variant="outline" onClick={() => set({ links: [...value.links, ''] })}>
              + 링크 추가
            </Button>
          </div>
        ) : null}
      </div>
      {problem ? <p className="text-bad text-sm">{problem}</p> : null}
    </div>
  );
}
